import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";

import { apiFetch, ApiError } from "@/lib/api";
import { formatNumber } from "@/lib/mrp";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import { ReadOnlyHoverLayer } from "@/components/ui/title-help";

function expectedArrivalForDateInput(value: string | null | undefined): string {
  if (value == null || value === "") return "";
  const trimmed = value.trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) return trimmed.slice(0, 10);
  return trimmed;
}

function parseSegmentQuantityInput(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  const parsed = Number.parseFloat(trimmed.replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : null;
}

function splitDeliverySaveDisabledHint(input: {
  hubWritesAllowed: boolean;
  planPending: boolean;
  planError: boolean;
  hasInvalidLine: boolean;
  hasEmptyLine: boolean;
  allPositive: boolean;
  hasOver: boolean;
  leftToAssign: number;
  totalAllocated: number;
  orderQuantity: number;
}): string {
  if (!input.hubWritesAllowed) return "";
  if (input.planPending || input.planError) return "Delivery plan is still loading.";
  if (input.hasInvalidLine) return "Enter a valid quantity for each segment.";
  if (input.hasEmptyLine) return "Each segment needs a quantity.";
  if (!input.allPositive) return "Each segment quantity must be greater than zero.";
  if (input.hasOver) {
    const overBy = input.totalAllocated - input.orderQuantity;
    return `Segment quantities total ${formatNumber(input.totalAllocated)} but order quantity is ${formatNumber(input.orderQuantity)}. Reduce by ${formatNumber(overBy)}.`;
  }
  if (input.leftToAssign > 1e-3) {
    return `Assign ${formatNumber(input.leftToAssign)} more across segments to match order quantity ${formatNumber(input.orderQuantity)}.`;
  }
  return "";
}

export interface PurchaseOrderSplitDeliveriesDialogProps {
  purchaseOrderItemId: number;
  cpn: string;
  prefillExpectedArrivalWhenNoSegments: string | null;
  hubWritesAllowed: boolean;
  hubReadOnlyTitle: string;
  onClose: () => void;
  onSave: (segments: { quantity: number; expected_arrival: string | null }[]) => void;
  isSaving: boolean;
  saveError: Error | ApiError | null;
}

export function PurchaseOrderSplitDeliveriesDialog({
  purchaseOrderItemId,
  cpn,
  prefillExpectedArrivalWhenNoSegments,
  hubWritesAllowed,
  hubReadOnlyTitle,
  onClose,
  onSave,
  isSaving,
  saveError,
}: PurchaseOrderSplitDeliveriesDialogProps): JSX.Element {
  const planQuery = useQuery({
    queryKey: ["mrp", "po-delivery-plan", purchaseOrderItemId],
    queryFn: () =>
      apiFetch<{
        order_quantity: number;
        quantity_received: number;
        line_remaining: number;
        segments: { id: number; quantity: number; expected_arrival: string | null; sort_order: number }[];
      }>(`/api/mrp/mv-purchase-order-items/${purchaseOrderItemId}/delivery-plan`),
  });

  const [draft, setDraft] = useState<{ quantity: string; expected_arrival: string }[]>([]);
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (!planQuery.data) return;
    const { order_quantity: orderQuantity, segments } = planQuery.data;
    if (segments.length === 0) {
      setDraft([
        {
          quantity: String(orderQuantity),
          expected_arrival: expectedArrivalForDateInput(prefillExpectedArrivalWhenNoSegments),
        },
      ]);
    } else {
      setDraft(
        segments.map((segment) => ({
          quantity: String(segment.quantity),
          expected_arrival: segment.expected_arrival
            ? expectedArrivalForDateInput(segment.expected_arrival)
            : "",
        })),
      );
    }
    setLocalError(null);
  }, [planQuery.data, purchaseOrderItemId, prefillExpectedArrivalWhenNoSegments]);

  const orderQty = planQuery.data?.order_quantity ?? 0;
  const lineRemaining = planQuery.data?.line_remaining ?? 0;

  const splitQuantitySummary = useMemo(() => {
    if (!planQuery.data) {
      return {
        perLine: [] as { value: number | null; hasInvalid: boolean }[],
        totalAllocated: 0,
        canSave: false,
        leftToAssign: 0,
        isBalanced: false,
        hasOver: false,
        hasEmptyLine: false,
        hasInvalidLine: false,
        allPositive: false,
      };
    }
    const target = planQuery.data.order_quantity;
    const perLine: { value: number | null; hasInvalid: boolean }[] = draft.map((line) => {
      const trimmed = line.quantity.trim();
      if (trimmed === "") return { value: null, hasInvalid: false };
      const parsed = parseSegmentQuantityInput(line.quantity);
      if (parsed == null) return { value: null, hasInvalid: true };
      return { value: parsed, hasInvalid: false };
    });

    const hasInvalidLine = perLine.some((entry) => entry.hasInvalid);
    const hasEmptyLine = perLine.some((entry) => entry.value == null && !entry.hasInvalid);
    let totalAllocated = 0;
    for (const entry of perLine) {
      if (entry.value != null) totalAllocated += entry.value;
    }

    const allPositive = perLine.length > 0 && perLine.every((entry) => entry.value != null && entry.value > 0);
    const isBalanced = Math.abs(totalAllocated - target) < 1e-3;
    const hasOver = totalAllocated - target > 1e-3;
    const leftToAssign = target - totalAllocated;
    const canSave = !hasInvalidLine && !hasEmptyLine && allPositive && isBalanced;

    return {
      perLine,
      totalAllocated,
      canSave,
      leftToAssign,
      isBalanced,
      hasOver,
      hasEmptyLine,
      hasInvalidLine,
      allPositive,
    };
  }, [planQuery.data, draft]);

  const saveMessage =
    saveError instanceof ApiError ? saveError.message : saveError != null ? saveError.message : null;

  const saveDisabledHint = splitDeliverySaveDisabledHint({
    hubWritesAllowed,
    planPending: planQuery.isPending,
    planError: planQuery.isError,
    hasInvalidLine: splitQuantitySummary.hasInvalidLine,
    hasEmptyLine: splitQuantitySummary.hasEmptyLine,
    allPositive: splitQuantitySummary.allPositive,
    hasOver: splitQuantitySummary.hasOver,
    leftToAssign: splitQuantitySummary.leftToAssign,
    totalAllocated: splitQuantitySummary.totalAllocated,
    orderQuantity: orderQty,
  });

  const saveDisabled =
    !hubWritesAllowed || planQuery.isPending || planQuery.isError || !splitQuantitySummary.canSave;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="split-delivery-title"
    >
      <Card className="max-h-[90vh] w-full max-w-lg overflow-y-auto shadow-xl">
        <CardHeader>
          <h3 id="split-delivery-title" className="text-base font-semibold text-slate-900">
            Split delivery dates
          </h3>
        </CardHeader>
        <CardBody className="space-y-3">
          <p className="text-sm text-slate-600">
            <span className="font-mono">{cpn}</span>
            {" · "}
            Order quantity <span className="font-mono">{formatNumber(orderQty)}</span>
            {planQuery.data ? (
              <>
                {" "}
                ({formatNumber(planQuery.data.quantity_received)} received,{" "}
                <span className="font-mono">{formatNumber(lineRemaining)}</span> still open on the line). All segment
                quantities must add up to that order quantity.
              </>
            ) : null}
          </p>
          {!planQuery.isPending && planQuery.data && draft.length > 1 ? (
            <p className="text-sm text-slate-600">
              To merge back to one table row, remove extra segments with{" "}
              <span className="font-medium text-slate-800">✕</span>, set the remaining segment to the full order
              quantity, and Save. Matching dates alone does not merge.
            </p>
          ) : null}
          {planQuery.isPending ? <p className="text-sm text-slate-500">Loading…</p> : null}
          {planQuery.isError ? (
            <p className="text-sm text-rose-700">{(planQuery.error as Error).message}</p>
          ) : null}
          {!planQuery.isPending && planQuery.data ? (
            <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={hubReadOnlyTitle} layout="block">
              <div className="space-y-2">
                {draft.map((line, index) => {
                  const perLine = splitQuantitySummary.perLine[index]!;
                  return (
                    <div
                      key={index}
                      className={cn(
                        "grid items-end gap-x-2 border-b border-slate-100 pb-2",
                        draft.length > 1
                          ? "grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)_2.5rem]"
                          : "grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]",
                      )}
                    >
                      <div className="min-w-0">
                        <Label>Quantity</Label>
                        <Input
                          type="text"
                          inputMode="decimal"
                          value={line.quantity}
                          disabled={!hubWritesAllowed}
                          onChange={(event) => {
                            setLocalError(null);
                            setDraft((current) => {
                              const next = [...current];
                              next[index] = { ...next[index]!, quantity: event.target.value };
                              return next;
                            });
                          }}
                          className={cn("h-9 text-xs", perLine.hasInvalid && "border-rose-500")}
                        />
                      </div>
                      <div className="min-w-0">
                        <Label>Expected arrival</Label>
                        <Input
                          type="date"
                          value={line.expected_arrival}
                          disabled={!hubWritesAllowed}
                          onChange={(event) => {
                            const next = [...draft];
                            next[index] = { ...next[index]!, expected_arrival: event.target.value };
                            setDraft(next);
                          }}
                          className="h-9 text-xs"
                        />
                      </div>
                      {draft.length > 1 ? (
                        <div className="flex h-9 items-center justify-end">
                          <button
                            type="button"
                            disabled={!hubWritesAllowed}
                            onClick={() => {
                              setDraft((current) => current.filter((_, rowIndex) => rowIndex !== index));
                            }}
                            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                            aria-label="Remove segment"
                          >
                            <X className="h-4 w-4" strokeWidth={2} />
                          </button>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    type="button"
                    disabled={!hubWritesAllowed}
                    onClick={() => {
                      setLocalError(null);
                      setDraft((current) => [...current, { quantity: "", expected_arrival: "" }]);
                    }}
                  >
                    Add segment
                  </Button>
                </div>
                <p
                  className={cn(
                    "text-sm font-medium tabular-nums",
                    splitQuantitySummary.isBalanced
                      ? "text-slate-700"
                      : splitQuantitySummary.hasOver
                        ? "text-rose-700"
                        : "text-amber-800",
                  )}
                >
                  Allocated{" "}
                  <span className="font-mono">{formatNumber(splitQuantitySummary.totalAllocated)}</span>
                  {" / "}
                  <span className="font-mono">{formatNumber(orderQty)}</span>
                  {splitQuantitySummary.isBalanced ? (
                    <> · matches order quantity</>
                  ) : splitQuantitySummary.hasOver ? (
                    <>
                      {" "}
                      ·{" "}
                      <span className="font-mono">
                        {formatNumber(splitQuantitySummary.totalAllocated - orderQty)}
                      </span>{" "}
                      over limit
                    </>
                  ) : (
                    <>
                      {" "}
                      ·{" "}
                      <span className="font-mono">{formatNumber(splitQuantitySummary.leftToAssign)}</span> still to
                      assign
                    </>
                  )}
                </p>
              </div>
            </ReadOnlyHoverLayer>
          ) : null}
          {localError ? <p className="text-sm text-rose-700">{localError}</p> : null}
          {saveMessage ? <p className="text-sm text-rose-700">{saveMessage}</p> : null}
          <div className="flex flex-wrap justify-end gap-2 border-t border-slate-100 pt-3">
            <Button size="sm" variant="secondary" type="button" disabled={isSaving} onClick={onClose}>
              Close
            </Button>
            <ReadOnlyHoverLayer
              active={!hubWritesAllowed}
              hoverHint={hubReadOnlyTitle}
              layout="inline"
            >
              <ReadOnlyHoverLayer
                active={hubWritesAllowed && saveDisabled && saveDisabledHint !== ""}
                hoverHint={saveDisabledHint}
                layout="inline"
              >
                <Button
                  size="sm"
                  variant="primary"
                  type="button"
                  isLoading={isSaving}
                  disabled={saveDisabled}
                  onClick={() => {
                  if (!hubWritesAllowed) return;
                  const parsed = draft.map((line) => ({
                    quantity: Number.parseFloat(line.quantity.replace(/,/g, "")),
                    expected_arrival: line.expected_arrival.trim() === "" ? null : line.expected_arrival.trim(),
                  }));
                  for (const item of parsed) {
                    if (!Number.isFinite(item.quantity) || item.quantity <= 0) {
                      setLocalError("Each segment needs a positive quantity.");
                      return;
                    }
                  }
                  const sum = parsed.reduce((accumulator, item) => accumulator + item.quantity, 0);
                  const target = planQuery.data?.order_quantity ?? 0;
                  if (Math.abs(sum - target) > 1e-3) {
                    setLocalError(`Segment quantities must sum to order quantity (${formatNumber(target)}).`);
                    return;
                  }
                  setLocalError(null);
                  onSave(parsed);
                }}
              >
                Save
              </Button>
              </ReadOnlyHoverLayer>
            </ReadOnlyHoverLayer>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
